import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CandidateLayout } from '@/components/candidate/CandidateLayout';
import { CommunityHeader } from '@/components/community/CommunityHeader';
import { CommunityBanner } from '@/components/community/CommunityBanner';
import { PinnedRule } from '@/components/community/PinnedRule';
import { CreatePost } from '@/components/community/CreatePost';
import { PostCard } from '@/components/community/PostCard';
import { PostSkeleton } from '@/components/community/PostSkeleton';
import { PostDetailDialog } from '@/components/community/PostDetailDialog';
import { Post } from '@/types/community.types';
import { usePageMeta } from '@/hooks/usePageMeta';

const Learning = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const postIdFromUrl = searchParams.get('post');
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  usePageMeta({
    title: "Aprendizado - Data Hire Club",
    description: "Recursos de aprendizado da comunidade",
    noindex: true,
  });
  
  // Fetch posts for learning
  const { 
    data: posts = [], 
    isLoading, 
    refetch: refetchPosts 
  } = useQuery({
    queryKey: ['learning-posts'],
    queryFn: async () => {
      console.log('Fetching learning posts...');
      
      const { data, error } = await supabase
        .from('community_posts')
        .select(`
          id,
          content,
          created_at,
          updated_at,
          likes_count,
          comments_count,
          image_urls,
          gif_url,
          link_url,
          public_profiles!inner(
            id,
            full_name,
            logo_url
          ),
          community_post_likes!left(
            user_id
          )
        `)
        .eq('post_type', 'learning')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching posts:', error);
        throw error;
      }

      console.log('Fetched posts:', data);

      const { data: { user } } = await supabase.auth.getUser();
      
      const formattedPosts = (data || []).map((post: any) => ({
        id: post.id,
        content: post.content,
        created_at: post.created_at,
        updated_at: post.updated_at,
        likes_count: post.likes_count || 0,
        comments_count: post.comments_count || 0,
        image_urls: Array.isArray(post.image_urls) ? post.image_urls as string[] : [],
        gif_url: post.gif_url || undefined,
        link_url: post.link_url || undefined,
        author: {
          id: post.public_profiles.id,
          full_name: post.public_profiles.full_name,
          logo_url: post.public_profiles.logo_url || ''
        },
        is_liked: user ? post.community_post_likes?.some((like: any) => like.user_id === user.id) : false
      })) as Post[];

      return formattedPosts;
    }
  });

  const handlePostSuccess = async () => {
    await refetchPosts();
  };

  const handlePostUpdate = async () => {
    await refetchPosts();
  };

  // Open dialog when post ID is in URL
  useEffect(() => {
    if (postIdFromUrl && posts.length > 0) {
      const post = posts.find(p => p.id === postIdFromUrl);
      if (post) {
        setSelectedPost(post);
        setDetailDialogOpen(true);
      }
    }
  }, [postIdFromUrl, posts]);

  const handleDialogClose = (open: boolean) => {
    setDetailDialogOpen(open);
    if (!open) {
      setSearchParams({});
      setSelectedPost(null);
    }
  };

  return (
    <CandidateLayout maxWidth="4xl">
      <div className="bg-gray-50 rounded-lg p-6 md:p-8">
        <CommunityHeader 
          title="Conhecimento Técnico" 
          description="Compartilhe recursos, tutoriais e discussões técnicas sobre dados"
        />

        <CommunityBanner type="LEARNING" />
        
        <PinnedRule type="learning" content="💡 Compartilhe ferramentas, técnicas, tutoriais e conhecimentos técnicos da área de dados!" />

        <CreatePost 
          onPostSuccess={handlePostSuccess}
          placeholder="Compartilhe conhecimento técnico, ferramentas ou tutoriais..."
        />

        {/* Posts */}
        <div className="space-y-4">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <PostSkeleton key={index} />
            ))
          ) : posts.length > 0 ? (
            posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                onLikeChange={handlePostUpdate}
                onPostDelete={handlePostUpdate}
              />
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              Ainda não há posts técnicos. Seja o primeiro a compartilhar conhecimento!
            </div>
          )}
        </div>
      </div>

      {selectedPost && (
        <PostDetailDialog
          post={selectedPost}
          open={detailDialogOpen}
          onOpenChange={handleDialogClose}
          onLikeChange={handlePostUpdate}
        />
      )}
    </CandidateLayout>
  );
};

export default Learning;
